import {
  QuoteReply,
  type QuoteStatus,
  type QuoteSummary,
  formatBRL,
  formatBps,
} from '@deizi/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon } from '../../components/Icon';
import { Sheet } from '../../components/Overlays';
import { PageHead } from '../../components/PageHead';
import { ErrorBox } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { useChannels, useTemplates } from '../../lib/catalog-api';
import { copyText, errorMessage } from '../../lib/messages';
import { quoteParams, useQuotes } from '../../lib/quotes-api';
import { useIsPhone } from '../../lib/use-media';
import { STATUS_BADGE, declineLabel, statusLabel, validityLabel } from './format';
import { ConvertDialog, DeclineDialog, useQuoteAction } from './quote-actions';
import { ExportCsvButton } from '../../components/ExportCsv';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

const SEGS: (QuoteStatus | null)[] = [
  null,
  'draft',
  'sent',
  'accepted',
  'declined',
  'expired',
  'converted',
];
const COLS = '120px 1.2fr 1.6fr 110px 100px 72px 110px 112px';

/** "Lembrar": the reminder template filled for this quote, copied to paste in the chat. */
function useCopyReminder() {
  const templates = useTemplates();
  const toast = useToast();
  return async (q: QuoteSummary) => {
    const reminder = templates.data?.data.find((t) => /lembrete/i.test(t.name));
    try {
      const reply = await api.get(
        `/quotes/${q.id}/reply${reminder ? `?templateId=${reminder.id}` : ''}`,
        QuoteReply,
      );
      const ok = await copyText(reply.text);
      const first = q.customer?.name.split(' ')[0] ?? 'o cliente';
      toast(ok ? `Lembrete para ${first} copiado. É só colar.` : 'Não deu para copiar.');
    } catch (err) {
      toast(errorMessage(err));
    }
  };
}

const segLabel = (s: QuoteStatus | null) => (s ? statusLabel(s) : 'Todos');

export function QuotesPage() {
  const phone = useIsPhone();
  const [status, setStatus] = useState<QuoteStatus | null>(null);
  const [channelId, setChannelId] = useState('');
  const [q, setQ] = useState('');
  const quotes = useQuotes({ status, channelId: channelId || null, q });
  const channels = useChannels();
  const action = useQuoteAction();
  const remind = useCopyReminder();
  const [sheet, setSheet] = useState<QuoteSummary | null>(null);
  const [declining, setDeclining] = useState<QuoteSummary | null>(null);
  const [converting, setConverting] = useState<QuoteSummary | null>(null);
  const navigate = useNavigate();

  const counts = quotes.data?.counts;
  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;
  const rows = quotes.data?.data ?? [];
  const filtered = status !== null || channelId !== '' || q.trim() !== '';
  const clear = () => {
    setStatus(null);
    setChannelId('');
    setQ('');
  };

  const segCount = (s: QuoteStatus | null) => (s ? (counts?.[s] ?? 0) : total);

  const empty =
    quotes.data && total === 0 && !filtered ? (
      <div className="card">
        <div className="empty">
          <div className="emptyart" />
          <h2>Nenhum orçamento ainda</h2>
          <p>
            Calcule o preço de uma peça no orçamento rápido e salve para acompanhar a resposta do
            cliente.
          </p>
          <Link className="btn p" to="/orcamento-rapido">
            Fazer o primeiro orçamento
          </Link>
        </div>
      </div>
    ) : null;
  const noMatch = (
    <div className="empty" style={{ padding: '32px 20px' }}>
      <h3>Nenhum orçamento com esses filtros</h3>
      <p className="sm">Troque a situação, o canal ou a busca.</p>
      <button type="button" className="btn sm" onClick={clear}>
        Limpar filtros
      </button>
    </div>
  );

  if (quotes.error && !quotes.data) {
    return (
      <>
        <PageHead crumb="Vender" title="Orçamentos" />
        <div className="card">
          <ErrorBox
            title="Não foi possível carregar os orçamentos"
            onRetry={() => void quotes.refetch()}
          />
        </div>
      </>
    );
  }

  if (phone) {
    return (
      <div className="col" style={{ gap: 12 }}>
        <Link className="btn p block" to="/orcamento-rapido" style={{ minHeight: 52 }}>
          <Icon name="plus" size={18} />
          Novo orçamento
        </Link>
        {!quotes.data ? (
          <div className="col" style={{ gap: 12 }} aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skel" style={{ height: 104 }} />
            ))}
          </div>
        ) : (
          (empty ?? (
            <>
              <label className="search" style={{ maxWidth: 'none', flex: '0 0 auto', height: 48 }}>
                <Icon name="search" size={18} />
                <input
                  aria-label="Buscar orçamento"
                  placeholder="Nº, cliente ou item"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </label>
              <div className="chips" role="tablist" aria-label="Situação">
                {SEGS.map((s) => (
                  <button
                    key={s ?? 'all'}
                    type="button"
                    role="tab"
                    aria-selected={status === s}
                    className={status === s ? 'chip on' : 'chip'}
                    onClick={() => setStatus(s)}
                  >
                    {segLabel(s)}{' '}
                    <span className="tab" style={{ opacity: 0.75 }}>
                      {segCount(s)}
                    </span>
                  </button>
                ))}
              </div>
              {rows.map((r) => {
                const v = validityLabel(r);
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={v.warn ? 'qcard wl' : 'qcard'}
                    aria-haspopup="dialog"
                    aria-label={`${r.number}, ${r.customer?.name ?? 'sem cliente'}, ${statusLabel(r.status)}`}
                    onClick={() => setSheet(r)}
                  >
                    <span className="row between" style={{ gap: 8 }}>
                      <span className="tab" style={{ fontWeight: 600 }}>
                        {r.number}
                        {r.revision > 1 ? <span className="xs muted"> v{r.revision}</span> : null}
                      </span>
                      <span className={STATUS_BADGE[r.status]}>{statusLabel(r.status)}</span>
                    </span>
                    <span className="row between" style={{ gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span className="trunc" style={{ fontWeight: 500, display: 'block' }}>
                          {r.customer?.name ?? 'Sem cliente'}
                        </span>
                        <span className="trunc sm muted" style={{ display: 'block' }}>
                          {r.itemsSummary}
                        </span>
                      </span>
                      <span className="col" style={{ gap: 0, alignItems: 'flex-end' }}>
                        <span className="fr tab" style={{ fontSize: 17, color: 'var(--head)' }}>
                          {formatBRL(r.totals.priceCentavos)}
                        </span>
                        <span className="xs muted tab">margem {formatBps(r.totals.marginBps)}</span>
                      </span>
                    </span>
                    <span
                      className="row xs"
                      style={{
                        gap: 6,
                        color: v.warn ? 'var(--warn)' : 'var(--ink2)',
                        fontWeight: v.warn ? 500 : 400,
                      }}
                    >
                      {v.warn ? <Icon name="alert" size={14} /> : null}
                      {v.text === '—'
                        ? (declineLabel(r) ?? statusLabel(r.status))
                        : `Validade: ${v.text}`}
                    </span>
                  </button>
                );
              })}
              {rows.length === 0 ? <div className="card">{noMatch}</div> : null}
              <p className="xs muted" style={{ textAlign: 'center' }}>
                Mostrando {rows.length} de {total}
              </p>
            </>
          ))
        )}
        {sheet ? (
          <Sheet label={`Orçamento ${sheet.number}`} onClose={() => setSheet(null)}>
            <div className="row between" style={{ alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <p className="xs muted tab">
                  {sheet.number} · {sheet.channel?.name ?? 'sem canal'}
                </p>
                <h2 className="trunc">{sheet.customer?.name ?? 'Sem cliente'}</h2>
              </div>
            </div>
            <div className="card sunk col" style={{ gap: 0, padding: '6px 14px' }}>
              {(
                [
                  ['Itens', sheet.itemsSummary],
                  ['Total', formatBRL(sheet.totals.priceCentavos)],
                  ['Margem', formatBps(sheet.totals.marginBps)],
                  ['Validade', validityLabel(sheet).text],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="kv sm">
                  <span className="muted">{k}</span>
                  <span className="v" style={{ whiteSpace: 'normal', textAlign: 'right' }}>
                    {v}
                  </span>
                </div>
              ))}
              <div className="kv sm">
                <span className="muted">Situação</span>
                <span className={STATUS_BADGE[sheet.status]}>{statusLabel(sheet.status)}</span>
              </div>
              {declineLabel(sheet) ? (
                <div className="kv sm">
                  <span className="muted">Motivo</span>
                  <span className="v">{declineLabel(sheet)}</span>
                </div>
              ) : null}
            </div>
            <div className="col" style={{ gap: 8 }}>
              {sheet.status === 'draft' ? (
                <button
                  type="button"
                  className="btn p block"
                  onClick={() => {
                    action.mutate({ id: sheet.id, action: 'send' });
                    setSheet(null);
                  }}
                >
                  Marcar como enviado
                </button>
              ) : null}
              {sheet.status === 'sent' ? (
                <button type="button" className="btn block" onClick={() => void remind(sheet)}>
                  Lembrar {sheet.customer?.name.split(' ')[0] ?? 'cliente'}
                </button>
              ) : null}
              {sheet.status === 'draft' || sheet.status === 'sent' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ color: 'var(--ok)' }}
                    onClick={() => {
                      action.mutate({ id: sheet.id, action: 'accept' });
                      setSheet(null);
                    }}
                  >
                    Marcar aceito
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ color: 'var(--err)' }}
                    onClick={() => {
                      setDeclining(sheet);
                      setSheet(null);
                    }}
                  >
                    Marcar recusado
                  </button>
                </div>
              ) : null}
              {sheet.status === 'accepted' ? (
                <button
                  type="button"
                  className="btn p block"
                  onClick={() => {
                    setConverting(sheet);
                    setSheet(null);
                  }}
                >
                  Converter em pedido
                </button>
              ) : null}
              {sheet.status === 'expired' || sheet.status === 'declined' ? (
                <button
                  type="button"
                  className="btn p block"
                  onClick={() => {
                    action.mutate({ id: sheet.id, action: 'reopen' });
                    setSheet(null);
                  }}
                >
                  Reabrir com nova validade
                </button>
              ) : null}
              <button
                type="button"
                className="btn g block"
                onClick={() => void navigate(`/orcamentos/${sheet.id}`)}
              >
                Abrir completo
              </button>
            </div>
          </Sheet>
        ) : null}
        {declining ? <DeclineDialog quote={declining} onClose={() => setDeclining(null)} /> : null}
        {converting ? (
          <ConvertDialog quote={converting} onClose={() => setConverting(null)} />
        ) : null}
      </div>
    );
  }

  return (
    <>
      <PageHead
        crumb="Vender"
        title="Orçamentos"
        actions={
          <div className="row wrap" style={{ gap: 10 }}>
            <ExportCsvButton
              dataset="orcamentos"
              params={quoteParams({ status, channelId: channelId || null, q })}
            />
            <Link className="btn p" to="/orcamento-rapido">
              <Icon name="plus" size={18} />
              Novo orçamento
            </Link>
          </div>
        }
      />
      {!quotes.data ? (
        <div
          className="col"
          aria-busy="true"
          aria-label="Carregando orçamentos"
          style={{ gap: 14 }}
        >
          <div className="grid s">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skel" style={{ height: 78 }} />
            ))}
          </div>
          <div className="card col" style={{ gap: 10 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="skel" style={{ height: 44 }} />
            ))}
          </div>
        </div>
      ) : (
        (empty ?? (
          <>
            <div className="grid s">
              {(
                [
                  ['Rascunhos', counts?.draft ?? 0],
                  ['Aguardando resposta', counts?.sent ?? 0],
                  ['Aceitos ou convertidos', (counts?.accepted ?? 0) + (counts?.converted ?? 0)],
                  ['Recusados ou expirados', (counts?.declined ?? 0) + (counts?.expired ?? 0)],
                ] as const
              ).map(([label, n]) => (
                <div key={label} className="card" style={{ padding: 16 }}>
                  <p className="xs muted">{label}</p>
                  <p className="fr tab" style={{ fontSize: 26 }}>
                    {n}
                  </p>
                </div>
              ))}
            </div>
            <div className="row wrap" style={{ gap: 10 }}>
              <div
                className="seg"
                role="tablist"
                aria-label="Situação"
                style={{ flexWrap: 'wrap' }}
              >
                {SEGS.map((s) => (
                  <button
                    key={s ?? 'all'}
                    type="button"
                    role="tab"
                    aria-selected={status === s}
                    className={status === s ? 'on' : ''}
                    onClick={() => setStatus(s)}
                  >
                    {segLabel(s)}{' '}
                    <span className="tab" style={{ opacity: 0.75 }}>
                      {segCount(s)}
                    </span>
                  </button>
                ))}
              </div>
              <Combobox
                ariaLabel="Canal"
                style={{ width: 200 }}
                value={channelId}
                options={[
                  { value: '', label: 'Todos os canais', pinned: 'top' },
                  ...(channels.data?.data ?? []).map(channelOption),
                ]}
                onChange={setChannelId}
              />
              <label className="input" style={{ width: 240 }}>
                <Icon name="search" size={18} />
                <input
                  className="qin"
                  aria-label="Buscar orçamento"
                  placeholder="Nº, cliente ou item"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </label>
            </div>
            <div className="card flat">
              <div className="gt" style={{ minWidth: 0 }}>
                <div className="qhead">
                  <div className="gtr head" style={{ gridTemplateColumns: COLS }}>
                    <span>Nº</span>
                    <span>Cliente</span>
                    <span>Itens</span>
                    <span>Canal</span>
                    <span className="num">Total</span>
                    <span className="num">Margem</span>
                    <span>Validade</span>
                    <span>Situação</span>
                  </div>
                  <div className="act" />
                </div>
                {rows.map((r) => {
                  const v = validityLabel(r);
                  const reason = declineLabel(r);
                  return (
                    <div key={r.id} className={v.warn ? 'qrow wl' : 'qrow'}>
                      <Link
                        className="gtr"
                        to={`/orcamentos/${r.id}`}
                        style={{ gridTemplateColumns: COLS }}
                        aria-label={`Abrir ${r.number}`}
                      >
                        <span className="tab" style={{ color: 'var(--link)', fontWeight: 500 }}>
                          {r.number}
                          {r.revision > 1 ? <span className="xs muted"> v{r.revision}</span> : null}
                        </span>
                        <span className="trunc">{r.customer?.name ?? '—'}</span>
                        <span className="sm trunc">{r.itemsSummary}</span>
                        <span className="sm trunc">{r.channel?.name ?? '—'}</span>
                        <span className="num">{formatBRL(r.totals.priceCentavos)}</span>
                        <span className="num">{formatBps(r.totals.marginBps)}</span>
                        <span
                          className="sm"
                          style={{
                            color: v.warn
                              ? 'var(--warn)'
                              : v.text === '—' || r.status === 'expired'
                                ? 'var(--ink2)'
                                : 'var(--ink)',
                            fontWeight: v.warn ? 500 : 400,
                          }}
                        >
                          {v.text}
                        </span>
                        <span>
                          <span className={STATUS_BADGE[r.status]}>
                            {r.status === 'accepted' ? <Icon name="check" size={14} /> : null}
                            {statusLabel(r.status)}
                          </span>
                          {reason ? (
                            <span className="xs muted" style={{ display: 'block', marginTop: 3 }}>
                              {reason}
                            </span>
                          ) : null}
                        </span>
                      </Link>
                      <div className="act">
                        {r.status === 'sent' ? (
                          <button type="button" className="btn sm" onClick={() => void remind(r)}>
                            Lembrar
                          </button>
                        ) : null}
                        {r.status === 'draft' ? (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => action.mutate({ id: r.id, action: 'send' })}
                          >
                            Marcar enviado
                          </button>
                        ) : null}
                        {r.status === 'accepted' ? (
                          <button
                            type="button"
                            className="btn sm p"
                            onClick={() => setConverting(r)}
                          >
                            Converter
                          </button>
                        ) : null}
                        {r.status === 'converted' && r.order ? (
                          <Link className="btn sm g" to={`/pedidos/${r.order.id}`}>
                            {r.order.number}
                          </Link>
                        ) : null}
                        {r.status === 'expired' || r.status === 'declined' ? (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => action.mutate({ id: r.id, action: 'reopen' })}
                          >
                            Reabrir
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                {rows.length === 0 ? noMatch : null}
              </div>
            </div>
            <p className="sm muted">
              Mostrando {rows.length} de {total}.
            </p>
          </>
        ))
      )}
    </>
  );
}
